import { useCallback, useMemo, useRef, useState } from 'react'
import { StudioArt } from '../types'

export interface UseStudioTourApi {
  isPlaying: boolean
  activeIndex: number
  play: () => void
  stop: () => void
  jumpTo: (index: number) => void
}

/** Guided-tour playback — steps through `arts` in order, dwelling on each per the room's dwell setting. */
export function useStudioTour(
  arts: StudioArt[],
  dwellSeconds: number,
  onStep: (art: StudioArt, index: number) => void,
): UseStudioTourApi {
  const [isPlaying, setIsPlaying] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const artsRef = useRef(arts)
  artsRef.current = arts
  // Always call the latest step handler — `play` is memoized without `onStep`
  // in deps (stable toggle), so a stale closure used to select each print
  // while flyTo still saw an empty/early artsApi and never moved the camera.
  const onStepRef = useRef(onStep)
  onStepRef.current = onStep

  const stop = useCallback(() => {
    setIsPlaying(false)
    setActiveIndex(-1)
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  const play = useCallback(() => {
    if (isPlaying) {
      stop()
      return
    }
    setIsPlaying(true)
    let i = 0
    const step = () => {
      const list = artsRef.current
      if (i >= list.length) {
        stop()
        return
      }
      setActiveIndex(i)
      onStepRef.current(list[i], i)
      i += 1
      timerRef.current = setTimeout(step, 1100 + dwellSeconds * 1000)
    }
    step()
  }, [isPlaying, dwellSeconds, stop])

  const jumpTo = useCallback((index: number) => {
    // Stop autoplay's timer without using stop() — stop() also resets
    // activeIndex to -1, which immediately un-highlighted whichever stop
    // the user just clicked (mockup keeps the clicked stop marked "now").
    setIsPlaying(false)
    if (timerRef.current) clearTimeout(timerRef.current)
    const art = artsRef.current[index]
    if (art) {
      setActiveIndex(index)
      onStepRef.current(art, index)
    }
  }, [])

  return useMemo(() => ({ isPlaying, activeIndex, play, stop, jumpTo }), [isPlaying, activeIndex, play, stop, jumpTo])
}
